import { useState, useEffect, useCallback } from "react";
import {
  Plus, Search, LayoutGrid, List, MoreVertical, BarChart3, LineChart,
  PieChart, Table2, TrendingUp, Play, Calendar, Clock, Copy, Trash2,
  Edit, Download, Loader2, Mail, ChevronRight, ChevronDown, Sparkles,
} from "lucide-react";
import {
  LineChart as ReLineChart, Line, BarChart as ReBarChart, Bar,
  PieChart as RePieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { reportsApi } from "@/lib/api";
import { HelpIcon } from "@/components/HelpIcon";
import { toast } from "sonner";

const REPORT_TYPES = [
  { id: "chart", name: "Chart Report", icon: BarChart3 },
  { id: "tabular", name: "Tabular Report", icon: Table2 },
  { id: "drilldown", name: "Drill-Down Report", icon: TrendingUp },
];

const CHART_TYPES = [
  { id: "line", name: "Line Chart", icon: LineChart },
  { id: "bar", name: "Bar Chart", icon: BarChart3 },
  { id: "pie", name: "Pie Chart", icon: PieChart },
  { id: "area", name: "Area Chart", icon: TrendingUp },
];

const ROLES = ["admin", "analyst", "developer", "billing", "viewer"];
const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

// ── Chart renderer ─────────────────────────────────────────────────────────────

function ReportChart({ data, chartType, reportType }) {
  if (!data || data.length === 0) {
    return <div className="text-center text-slate-400 text-sm">No data — click "Run Preview"</div>;
  }
  const keys = Object.keys(data[0]);
  const xKey = keys[0];
  const yKeys = keys.slice(1);
  const nameKey = "name" in data[0] ? "name" : xKey;
  const valKey = "value" in data[0] ? "value" : (yKeys[0] || xKey);

  if (reportType === "tabular") {
    return (
      <div className="overflow-auto h-full">
        <div className="bg-slate-50 sticky top-0 flex border-b border-slate-100">
          {keys.map(k => <div key={k} className="flex-1 min-w-0 px-2 py-1.5 text-left text-slate-500 uppercase text-xs font-medium">{k.replace(/_/g, " ")}</div>)}
        </div>
        {data.map((row, i) => (
          <div key={i} className="flex border-b border-slate-100 hover:bg-slate-50">
            {keys.map(k => <div key={k} className="flex-1 min-w-0 px-2 py-1.5 text-slate-700 text-xs truncate">{String(row[k] ?? "")}</div>)}
          </div>
        ))}
      </div>
    );
  }
  if (chartType === "pie") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <RePieChart><Pie data={data} dataKey={valKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius="60%" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>{data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></RePieChart>
      </ResponsiveContainer>
    );
  }
  if (chartType === "area") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <defs>{yKeys.map((k, i) => <linearGradient key={k} id={`g${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.2} /><stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} /></linearGradient>)}</defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey={xKey} tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 9 }} /><Tooltip />
          {yKeys.map((k, i) => <Area key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} fill={`url(#g${i})`} strokeWidth={2} />)}
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  if (chartType === "bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ReBarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey={xKey} tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 9 }} /><Tooltip />
          {yKeys.map((k, i) => <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} />)}
        </ReBarChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ReLineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey={xKey} tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 9 }} /><Tooltip />
        {yKeys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2} />)}
      </ReLineChart>
    </ResponsiveContainer>
  );
}

// ── Drill-Down table ────────────────────────────────────────────────────────────

function DrilldownView({ reportId }) {
  const [level, setLevel] = useState(0);
  const [breadcrumbs, setBreadcrumbs] = useState([{ label: "All Tenants", level: 0, parent_id: null }]);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [idField, setIdField] = useState(null);
  const [loading, setLoading] = useState(false);

  const drill = useCallback(async (lvl, parentId, parentLabel) => {
    setLoading(true);
    try {
      const res = await reportsApi.drilldown(reportId, { level: lvl, parent_id: parentId, parent_label: parentLabel || "" });
      setData(res.data || []);
      setColumns(res.columns || []);
      setIdField(res.id_field || null);
      setLevel(lvl);
      setBreadcrumbs(res.breadcrumbs || [{ label: "All Tenants", level: 0, parent_id: null }]);
    } catch (e) {
      toast.error(e.message || "Drill-down failed");
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { drill(0, null, ""); }, [drill]);

  const handleRowClick = (row) => {
    if (!idField || level >= 2) return;
    const parentId = row[idField] || row.id || row.owner_id;
    if (!parentId) return;
    drill(level + 1, parentId, row.name || row.email || parentId);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 px-1 py-2 shrink-0 flex-wrap">
        {breadcrumbs.map((bc, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3 h-3 text-slate-400" />}
            <button
              className={`text-xs rounded px-2 py-0.5 ${i === breadcrumbs.length - 1 ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-500 hover:bg-slate-100"}`}
              onClick={() => bc.level < level && drill(bc.level, bc.parent_id, bc.label)}
            >{bc.label}</button>
          </span>
        ))}
        {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
      </div>
      <div className="flex-1 overflow-auto">
        {data.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-8">No data at this level</div>
        ) : (
          <div>
            <div className="bg-slate-50 sticky top-0 flex border-b border-slate-100">
              {columns.map(c => <div key={c} className="flex-1 min-w-0 px-2 py-1.5 text-left text-slate-500 uppercase text-xs font-medium">{c.replace(/_/g, " ")}</div>)}
              {idField && level < 2 && <div className="w-8 px-2 py-1.5 text-right text-slate-500 uppercase text-xs font-medium">→</div>}
            </div>
            {data.map((row, i) => (
              <div key={i} className="flex border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => handleRowClick(row)}>
                {columns.map(c => <div key={c} className="flex-1 min-w-0 px-2 py-1.5 text-slate-700 text-xs truncate">{String(row[c] ?? "")}</div>)}
                {idField && level < 2 && (
                  <div className="w-8 px-2 py-1.5 flex items-center justify-end">
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {level < 2 && idField && (
        <p className="text-xs text-slate-400 px-1 py-1 shrink-0">Click any row to drill down to the next level</p>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ReportBuilder() {
  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Form state
  const [reportName, setReportName] = useState("");
  const [reportDesc, setReportDesc] = useState("");
  const [reportType, setReportType] = useState("chart");
  const [chartType, setChartType] = useState("line");
  const [dataSource, setDataSource] = useState("audit_logs");
  const [timeRange, setTimeRange] = useState("30d");
  const [groupBy, setGroupBy] = useState("day");
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleFreq, setScheduleFreq] = useState("daily");
  const [selectedRoles, setSelectedRoles] = useState(["admin"]);
  const [outputFormat, setOutputFormat] = useState("pdf");
  const [pipelineJson, setPipelineJson] = useState("");
  const [deliveryEmails, setDeliveryEmails] = useState("");

  // Data
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchReports = useCallback(async () => {
    try { setReports(await reportsApi.list()); }
    catch { toast.error("Failed to load reports"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const filtered = reports.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) &&
    (filterType === "all" || r.type === filterType)
  );

  const resetForm = () => {
    setReportName(""); setReportDesc(""); setReportType("chart"); setChartType("line");
    setDataSource("audit_logs"); setTimeRange("30d"); setGroupBy("day");
    setScheduleEnabled(false); setScheduleFreq("daily"); setSelectedRoles(["admin"]);
    setOutputFormat("pdf"); setPipelineJson(""); setDeliveryEmails("");
    setPreviewData(null);
  };

  const openCreate = () => { resetForm(); setSelectedReport(null); setDialogOpen(true); };

  const openEdit = (r) => {
    setReportName(r.name); setReportDesc(r.description || ""); setReportType(r.type);
    setChartType(r.chart_type || "line"); setDataSource(r.data_source || "audit_logs");
    setTimeRange(r.time_range || "30d"); setGroupBy(r.group_by || "day");
    setScheduleEnabled(r.schedule_enabled || false); setScheduleFreq(r.schedule_frequency || "daily");
    setSelectedRoles(r.access_roles || ["admin"]); setOutputFormat(r.output_format || "pdf");
    setPipelineJson(r.pipeline_json || ""); setDeliveryEmails((r.delivery_emails || []).join(", "));
    setSelectedReport(r); setPreviewData(null); setDialogOpen(true);
  };

  const toggleRole = (r) => setSelectedRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);

  const handleSave = async () => {
    if (!reportName.trim()) { toast.error("Report name is required"); return; }
    setSaving(true);
    try {
      const emails = deliveryEmails.split(",").map(e => e.trim()).filter(Boolean);
      const payload = {
        name: reportName, description: reportDesc, type: reportType,
        chart_type: chartType, data_source: dataSource, time_range: timeRange,
        group_by: groupBy, output_format: outputFormat, schedule_enabled: scheduleEnabled,
        schedule_frequency: scheduleFreq, access_roles: selectedRoles,
        pipeline_json: pipelineJson, delivery_emails: emails, delivery_format: "html",
      };
      if (selectedReport) {
        await reportsApi.update(selectedReport.id, payload);
        toast.success("Report updated");
      } else {
        await reportsApi.create(payload);
        toast.success("Report created");
      }
      setDialogOpen(false);
      fetchReports();
    } catch (e) {
      toast.error(e.message || "Failed to save report");
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async (reportId) => {
    setRunningId(reportId);
    try {
      const res = await reportsApi.run(reportId);
      toast.success(`Report ran — ${res.rows} row(s) returned`);
      fetchReports();
    } catch (e) {
      toast.error(e.message || "Failed to run report");
    } finally {
      setRunningId(null);
    }
  };

  const handleRunPreview = async () => {
    if (!selectedReport) { toast.error("Save the report first, then run preview"); return; }
    setPreviewLoading(true);
    try {
      const res = await reportsApi.run(selectedReport.id);
      setPreviewData(res.data || []);
      toast.success(`Preview: ${res.rows} row(s)`);
    } catch (e) {
      toast.error(e.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDelete = async (r) => {
    try { await reportsApi.remove(r.id); toast.success("Deleted"); fetchReports(); }
    catch (e) { toast.error(e.message || "Failed to delete"); }
  };

  const handleDuplicate = async (r) => {
    try {
      await reportsApi.create({ name: `${r.name} (Copy)`, description: r.description, type: r.type, chart_type: r.chart_type, data_source: r.data_source, time_range: r.time_range, group_by: r.group_by, output_format: r.output_format, pipeline_json: r.pipeline_json, schedule_enabled: false, schedule_frequency: r.schedule_frequency, access_roles: r.access_roles, delivery_emails: [], delivery_format: "html" });
      toast.success("Duplicated"); fetchReports();
    } catch (e) { toast.error(e.message || "Failed to duplicate"); }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await reportsApi.seedDefaults();
      toast.success(res.message);
      fetchReports();
    } catch (e) {
      toast.error(e.message || "Seeding failed");
    } finally {
      setSeeding(false);
    }
  };

  const getIcon = (type, ct) => {
    if (type === "tabular") return Table2;
    if (type === "drilldown") return TrendingUp;
    const c = CHART_TYPES.find(x => x.id === ct);
    return c ? c.icon : BarChart3;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading reports...
      </div>
    );
  }

  return (
    <div data-testid="report-builder-page">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="report-builder-title">Report Builder</h1>
            <HelpIcon featureTag="reports" portalType="admin" />
          </div>
          <p className="text-sm text-slate-500 mt-1">Build, schedule, and deliver data reports with custom MongoDB pipelines</p>
        </div>
        {reports.length === 0 && (
          <Button variant="outline" onClick={handleSeedDefaults} disabled={seeding}>
            {seeding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Seeding...</> : <><Sparkles className="w-4 h-4 mr-2" /> Load Default Reports</>}
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Chart Reports", icon: BarChart3, color: "text-blue-600", bg: "bg-blue-50", count: reports.filter(r => r.type === "chart").length },
          { label: "Tabular Reports", icon: Table2, color: "text-emerald-600", bg: "bg-emerald-50", count: reports.filter(r => r.type === "tabular").length },
          { label: "Drill-Down Reports", icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50", count: reports.filter(r => r.type === "drilldown").length },
          { label: "Scheduled", icon: Calendar, color: "text-amber-600", bg: "bg-amber-50", count: reports.filter(r => r.schedule_enabled).length },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${s.bg} rounded-lg flex items-center justify-center`}><s.icon className={`w-5 h-5 ${s.color}`} /></div>
              <div><div className="text-2xl font-bold text-slate-900">{s.count}</div><div className="text-xs text-slate-500">{s.label}</div></div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search reports..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="report-search" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {REPORT_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border rounded-md">
            <Button variant={viewMode === "grid" ? "secondary" : "ghost"} size="sm" onClick={() => setViewMode("grid")} className="rounded-r-none"><LayoutGrid className="w-4 h-4" /></Button>
            <Button variant={viewMode === "list" ? "secondary" : "ghost"} size="sm" onClick={() => setViewMode("list")} className="rounded-l-none"><List className="w-4 h-4" /></Button>
          </div>
          <Button onClick={openCreate} data-testid="create-report-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Report
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
          <BarChart3 className="w-10 h-10 mx-auto mb-3" />
          <p className="text-sm font-medium">{reports.length === 0 ? "No reports yet" : "No reports match your filter"}</p>
          {reports.length === 0 && (
            <div className="mt-4 flex gap-3 justify-center">
              <Button variant="outline" size="sm" onClick={handleSeedDefaults} disabled={seeding}>
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Load 3 Default Reports
              </Button>
              <Button size="sm" onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Create Manually</Button>
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      {filtered.length > 0 && viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="report-gallery-grid">
          {filtered.map(r => {
            const Icon = getIcon(r.type, r.chart_type);
            const isRunning = runningId === r.id;
            return (
              <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`report-card-${r.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${r.type === "chart" ? "bg-blue-100" : r.type === "tabular" ? "bg-emerald-100" : "bg-purple-100"}`}>
                      <Icon className={`w-5 h-5 ${r.type === "chart" ? "text-blue-600" : r.type === "tabular" ? "text-emerald-600" : "text-purple-600"}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 text-sm">{r.name}</h3>
                      <p className="text-xs text-slate-500">{REPORT_TYPES.find(t => t.id === r.type)?.name}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleRunNow(r.id)} disabled={isRunning}>
                        {isRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running...</> : <><Play className="w-4 h-4 mr-2" /> Run Now</>}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(r)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(r)}><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(r)}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="h-16 bg-slate-50 rounded-md mb-3 flex items-center justify-center">
                  <Icon className="w-8 h-8 text-slate-300" />
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.last_run ? new Date(r.last_run).toLocaleString() : "Never run"}</span>
                  <Badge variant={r.status === "published" ? "default" : "secondary"} className="text-[10px]">{r.status}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">{r.schedule}</span>
                  {r.delivery_emails?.length > 0 && (
                    <span className="flex items-center gap-1 text-blue-500"><Mail className="w-3 h-3" />{r.delivery_emails.length} recipient(s)</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {filtered.length > 0 && viewMode === "list" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="report-gallery-list">
          <div>
            <div className="grid grid-cols-[1fr_120px_90px_160px_100px_100px] bg-slate-50 border-b border-slate-200">
              {["Report","Type","Status","Last Run","Schedule","Actions"].map(h => (
                <div key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
              ))}
            </div>
            {filtered.map(r => {
              const Icon = getIcon(r.type, r.chart_type);
              const isRunning = runningId === r.id;
              return (
                <div key={r.id} className="grid grid-cols-[1fr_120px_90px_160px_100px_100px] border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="px-4 py-3 flex items-center gap-3"><Icon className="w-4 h-4 text-slate-400" /><span className="text-sm font-medium text-slate-900">{r.name}</span></div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{REPORT_TYPES.find(t => t.id === r.type)?.name}</div>
                  <div className="px-4 py-3 flex items-center"><Badge variant={r.status === "published" ? "default" : "secondary"} className="text-[10px]">{r.status}</Badge></div>
                  <div className="px-4 py-3 text-sm text-slate-500 flex items-center">{r.last_run ? new Date(r.last_run).toLocaleString() : "Never"}</div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{r.schedule}</div>
                  <div className="px-4 py-3 flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleRunNow(r.id)} disabled={isRunning}>
                      {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(r)}><Edit className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl h-[88vh] flex flex-col p-0" aria-describedby="report-dialog-desc">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle>{selectedReport ? `Edit: ${selectedReport.name}` : "Create New Report"}</DialogTitle>
            <DialogDescription id="report-dialog-desc">Configure report type, data source, custom pipeline, scheduling, and email delivery</DialogDescription>
          </DialogHeader>

          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Left panel */}
            <div className="w-72 border-r p-4 space-y-4 overflow-y-auto shrink-0">
              <div>
                <Label className="text-xs">Report Name</Label>
                <Input value={reportName} onChange={(e) => setReportName(e.target.value)} placeholder="Monthly Activity Report" className="mt-1" data-testid="report-name-input" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea value={reportDesc} onChange={(e) => setReportDesc(e.target.value)} className="mt-1" rows={2} />
              </div>
              <div>
                <Label className="text-xs">Report Type</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger className="mt-1" data-testid="report-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REPORT_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {reportType === "chart" && (
                <div>
                  <Label className="text-xs">Chart Type</Label>
                  <Select value={chartType} onValueChange={setChartType}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CHART_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="pt-3 border-t">
                <Label className="text-xs">Output Format</Label>
                <Select value={outputFormat} onValueChange={setOutputFormat}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[["pdf","PDF Document"],["excel","Excel Spreadsheet"],["csv","CSV File"],["json","JSON Data"]].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="pt-3 border-t">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Schedule Delivery</Label>
                  <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} />
                </div>
                {scheduleEnabled && (
                  <>
                    <Select value={scheduleFreq} onValueChange={setScheduleFreq}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{[["hourly","Hourly"],["daily","Daily"],["weekly","Weekly"],["monthly","Monthly"]].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="mt-3">
                      <Label className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Delivery Emails</Label>
                      <Input value={deliveryEmails} onChange={(e) => setDeliveryEmails(e.target.value)} placeholder="admin@co.com, cto@co.com" className="mt-1 text-xs" data-testid="delivery-emails-input" />
                      <p className="text-xs text-slate-400 mt-1">Comma-separated email addresses</p>
                    </div>
                  </>
                )}
              </div>
              <div className="pt-3 border-t">
                <Label className="text-xs mb-2 block">Access Roles</Label>
                <div className="space-y-2">
                  {ROLES.map(r => (
                    <div key={r} className="flex items-center gap-2">
                      <Checkbox checked={selectedRoles.includes(r)} onCheckedChange={() => toggleRole(r)} id={`role-${r}`} />
                      <label htmlFor={`role-${r}`} className="text-sm text-slate-700 capitalize">{r}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right panel */}
            <div className="flex-1 flex flex-col min-h-0">
              <Tabs defaultValue={reportType === "drilldown" ? "drilldown" : "preview"} className="flex-1 flex flex-col">
                <div className="px-4 pt-4 border-b shrink-0">
                  <TabsList>
                    <TabsTrigger value="preview">Preview</TabsTrigger>
                    <TabsTrigger value="pipeline">Pipeline / Query</TabsTrigger>
                    <TabsTrigger value="data">Data Source</TabsTrigger>
                    {reportType === "drilldown" && selectedReport && <TabsTrigger value="drilldown">Drill-Down</TabsTrigger>}
                  </TabsList>
                </div>

                <TabsContent value="preview" className="flex-1 p-4 m-0 bg-slate-50 flex flex-col min-h-0">
                  <div className="flex items-center justify-between mb-3 shrink-0">
                    <span className="text-xs text-slate-500">Live data from your saved pipeline</span>
                    {selectedReport && (
                      <Button size="sm" variant="secondary" onClick={handleRunPreview} disabled={previewLoading} data-testid="run-preview-btn">
                        {previewLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</> : <><Play className="w-3 h-3 mr-1" /> Run Preview</>}
                      </Button>
                    )}
                    {!selectedReport && <span className="text-xs text-slate-400">Save the report first to run a preview</span>}
                  </div>
                  <div className="flex-1 bg-white border rounded-lg flex items-center justify-center p-4 min-h-0" data-testid="report-preview">
                    <ReportChart data={previewData} chartType={chartType} reportType={reportType} />
                  </div>
                </TabsContent>

                <TabsContent value="pipeline" className="flex-1 p-4 m-0 flex flex-col min-h-0 overflow-auto">
                  <div className="shrink-0 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs">Custom MongoDB Aggregation Pipeline</Label>
                      <Badge variant="outline" className="text-[10px]">Optional — overrides preset aggregation</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Leave empty to use the preset aggregation for the selected data source. Or write your own pipeline JSON array for full control.</p>
                  </div>
                  <Textarea
                    value={pipelineJson}
                    onChange={(e) => setPipelineJson(e.target.value)}
                    placeholder={`[\n  { "$group": { "_id": "$role", "count": { "$sum": 1 } } },\n  { "$project": { "_id": 0, "name": "$_id", "value": "$count" } }\n]`}
                    className="flex-1 font-mono text-xs resize-none min-h-[280px] bg-slate-900 text-emerald-300 border-slate-700 placeholder:text-slate-600"
                    data-testid="report-pipeline-editor"
                  />
                  <p className="text-xs text-slate-400 mt-2 shrink-0">
                    Collections: <code className="bg-slate-100 px-1 rounded">audit_logs</code>{" "}
                    <code className="bg-slate-100 px-1 rounded">users</code>{" "}
                    <code className="bg-slate-100 px-1 rounded">tenants</code>{" "}
                    <code className="bg-slate-100 px-1 rounded">api_keys</code>
                  </p>
                </TabsContent>

                <TabsContent value="data" className="flex-1 p-4 m-0 overflow-auto">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs">Data Source (used when no custom pipeline is set)</Label>
                      <Select value={dataSource} onValueChange={setDataSource}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="audit_logs">Audit Logs</SelectItem>
                          <SelectItem value="usage_metrics">Usage Metrics (Tenants)</SelectItem>
                          <SelectItem value="billing_data">Billing Data</SelectItem>
                          <SelectItem value="users">User Data</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Time Range</Label>
                      <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[["24h","Last 24h"],["7d","Last 7 days"],["30d","Last 30 days"],["90d","Last 90 days"]].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Group By</Label>
                      <Select value={groupBy} onValueChange={setGroupBy}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[["hour","Hour"],["day","Day"],["week","Week"],["month","Month"]].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                {reportType === "drilldown" && selectedReport && (
                  <TabsContent value="drilldown" className="flex-1 p-4 m-0 min-h-0 bg-slate-50">
                    <div className="bg-white border rounded-lg h-full p-3" data-testid="drilldown-view">
                      <DrilldownView reportId={selectedReport.id} />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="publish-report-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Download className="w-4 h-4 mr-2" /> {selectedReport ? "Save Changes" : "Publish Report"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
